/**
 * navigation/authStack.tsx
 * The authentication flow as a native-stack: login · signup · forgot/reset ·
 * email verification · check-email · resend-verification. The root navigator
 * mounts this for unauthenticated (or unverified) sessions and chooses the
 * initial route — from an email deep link or from the boot probe.
 */

import React from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

import type { AuthStackParamList } from './types';
import { ForcedLightThemeProvider } from '../context/themeContext';

import Login_Screen from '../screens/auth/login';
import Signup_Screen from '../screens/auth/signup';
import Forgot_Password_Screen from '../screens/auth/forgotPassword';
import Reset_Password_Screen from '../screens/auth/resetPassword';
import Verify_Email_Screen from '../screens/auth/verifyEmail';
import Check_Email_Screen from '../screens/auth/checkEmail';
import Resend_Verification_Screen from '../screens/auth/resendVerification';
import Personal_Info_Screen from '../screens/auth/personalInfo';
import Mfa_Challenge_Screen from '../screens/auth/mfaChallenge';
import Invitation_Accept_Screen from '../screens/auth/invitationAccept';

const Stack = createNativeStackNavigator<AuthStackParamList>();

interface AuthStackProps {
  /** Where the stack opens — set by the root navigator (deep link or boot probe). */
  initialRouteName: keyof AuthStackParamList;
  /** Params for the initial route only (e.g. the verify/reset token, or the email). */
  initialParams?: Record<string, unknown>;
}

export default function AuthStack({ initialRouteName, initialParams }: AuthStackProps) {
  // initialParams only ever applies to the route the stack opens on; attaching
  // it to the matching screen keeps the deep-link token / email available on
  // first render without leaking into routes reached by later navigation.
  const paramsFor = (route: keyof AuthStackParamList) =>
    route === initialRouteName ? initialParams : undefined;

  // Auth screens always render on a stable white surface — force the light
  // theme for the whole flow so dark mode never bleeds into sign-in/sign-up.
  return (
    <ForcedLightThemeProvider>
      <Stack.Navigator
        initialRouteName={initialRouteName}
        screenOptions={{ headerShown: false, animation: 'none' }}
      >
        <Stack.Screen name="login" component={Login_Screen} />
        <Stack.Screen name="signup" component={Signup_Screen} />
        <Stack.Screen name="forgotPassword" component={Forgot_Password_Screen} />
        <Stack.Screen name="resetPassword" component={Reset_Password_Screen} initialParams={paramsFor('resetPassword')} />
        <Stack.Screen name="verifyEmail" component={Verify_Email_Screen} initialParams={paramsFor('verifyEmail')} />
        <Stack.Screen name="checkEmail" component={Check_Email_Screen} initialParams={paramsFor('checkEmail')} />
        <Stack.Screen name="resendVerification" component={Resend_Verification_Screen} initialParams={paramsFor('resendVerification')} />
        <Stack.Screen name="personalInfo" component={Personal_Info_Screen} />
        <Stack.Screen name="mfaChallenge" component={Mfa_Challenge_Screen} initialParams={paramsFor('mfaChallenge')} />
        <Stack.Screen name="invitationAccept" component={Invitation_Accept_Screen} initialParams={paramsFor('invitationAccept')} />
      </Stack.Navigator>
    </ForcedLightThemeProvider>
  );
}
